import { z } from "zod"

import { TenantIdSchema } from "./auth.js"
import {
	AttachmentIdSchema,
	CorrectionRoundIdSchema,
	DocumentIdSchema,
	DocumentTypeIdSchema,
	ShipmentIdSchema,
} from "./id-schemas.js"

/**
 * Document — instance of a DocumentType (BL, OBL, SI, Invoice, Packing
 * List, CoO, Phyto, Fumigation, Quality, etc.) attached to a Shipment.
 *
 * Status reflects the lifecycle from PRD §5: drafted -> pending review
 * -> confirmed (by Documentation role) -> final. Correction rounds are
 * first-class and link back via `correctionRoundIds`. Real evidence:
 * NC17 = 17 correction rounds on a single BL.
 */

const IsoDateTime = z.string().datetime()

export const DocumentStatusSchema = z.enum([
	"DRAFT",
	"PENDING",
	"UNDER_REVIEW",
	"CORRECTION_REQUESTED",
	"CONFIRMED",
	"FINAL",
	"CANCELLED",
])
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>

export const DocumentSchema = z.object({
	id: DocumentIdSchema,
	tenantId: TenantIdSchema,
	shipmentId: ShipmentIdSchema,
	documentTypeId: DocumentTypeIdSchema,
	status: DocumentStatusSchema,
	version: z.number().int().nonnegative(),
	attachmentIds: z.array(AttachmentIdSchema),
	correctionRoundIds: z.array(CorrectionRoundIdSchema),
	dueAt: IsoDateTime.optional(),
	completedAt: IsoDateTime.optional(),
	createdAt: IsoDateTime,
	updatedAt: IsoDateTime,
})
export type Document = z.infer<typeof DocumentSchema>
